import { useMemo, useState } from 'react';
import { bucketByDay } from '../../lib/calendar/timeGridLayout';
import { dayKeyParts, instantAt, isSameMonth, minutesOf, monthCells, MONTH_NAMES, WEEKDAY_NAMES } from '../../lib/calendar/zonedCalendar';
import { EventTagChip } from './EventTagChip';
import { DayOverflowPopover } from './DayOverflowPopover';

/**
 * Chips per cell before "+N more". Re-derived for the #679 cell rather than
 * inherited: a cell is min 118px; the date row takes ~28px, leaving 90px. A
 * chip is ~19.5px (2px×2 padding + a 10.5px line at 1.45 + 3px gap) and the
 * "+N more" line ~15px, so ⌊(90 − 15) / 19.5⌋ = 3. It happens to match the
 * old constant and the prototype — the point is that it is now derived.
 */
export const MAX_CHIPS_PER_CELL = 3;

/**
 * Month view (#679). Six Sunday-start weeks; cells bucketed by the DISPLAY
 * zone (the old grid bucketed by browser-local date).
 *
 * Two click targets per cell, deliberately distinct:
 *   - the day NUMBER drills into Day view (kept behaviour);
 *   - the empty cell starts a new event at 9 AM (prototype).
 *
 * Props:
 *   anchorDay, todayKey, displayZone, events
 *   onEventClick(event)
 *   onDayClick(dayKey)            — number / "Open Day view"
 *   onCellCreate?(startInstant)   — omit to disable click-to-create
 *   createDisabledReason?: string
 *   onRescheduleDraft({event,newStart,newEnd})
 */
export function CalendarMonthGrid({
    anchorDay, todayKey, displayZone, events, onEventClick, onDayClick, onCellCreate,
    createDisabledReason, onRescheduleDraft,
}) {
    const [dragOverKey, setDragOverKey] = useState(null);
    const cells = useMemo(() => monthCells(anchorDay), [anchorDay]);
    const buckets = useMemo(() => bucketByDay(events, cells, displayZone), [events, cells, displayZone]);

    const handleDrop = (e, dayKey) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverKey(null);
        const raw = e.dataTransfer.getData('application/json');
        if (!raw) return;
        try {
            const { eventId, duration } = JSON.parse(raw);
            const event = (events || []).find(ev => ev.event_id === eventId);
            if (!event) return;
            // Keep the time of day (in the display zone); only the date moves.
            const newStart = instantAt(dayKey, minutesOf(event.start, displayZone), displayZone);
            const newEnd = new Date(newStart.getTime() + duration);
            onRescheduleDraft?.({ event, newStart: newStart.toISOString(), newEnd: newEnd.toISOString() });
        } catch { /* a drag from outside the calendar */ }
    };

    return (
        <div className="flex-1 min-h-0 min-w-0 flex flex-col overflow-x-auto" data-testid="calendar-month-grid">
            <div className="flex flex-none min-w-[880px] border-b border-[#EDEBE6] bg-white">
                {WEEKDAY_NAMES.map(w => (
                    <div key={w} className="flex-1 min-w-0 px-3 py-2 border-r border-[#F2F0EC] text-[10.5px] font-semibold tracking-[0.08em] uppercase text-[#A29B95]">
                        {w.slice(0, 3)}
                    </div>
                ))}
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
                <div className="grid min-w-[880px] min-h-full" style={{ gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gridAutoRows: 'minmax(118px, 1fr)' }}>
                    {cells.map(key => {
                        const p = dayKeyParts(key);
                        const dim = !isSameMonth(key, anchorDay);
                        const today = key === todayKey;
                        const dayEvents = buckets[key] || [];
                        const visible = dayEvents.slice(0, MAX_CHIPS_PER_CELL);
                        const overflow = dayEvents.length - visible.length;
                        return (
                            <div
                                key={key}
                                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverKey(key); }}
                                onDragLeave={() => setDragOverKey(prev => (prev === key ? null : prev))}
                                onDrop={(e) => handleDrop(e, key)}
                                onClick={onCellCreate ? () => onCellCreate(instantAt(key, 9 * 60, displayZone)) : undefined}
                                title={onCellCreate ? `New event · ${p.day} ${MONTH_NAMES[p.month]}` : createDisabledReason}
                                className={`min-w-0 box-border overflow-hidden px-[9px] pt-[7px] pb-[9px] border-r border-b border-[#F2F0EC] ${dim ? 'bg-[#FCFBFA]' : today ? 'bg-[#FBFAFE]' : ''} ${onCellCreate ? 'cursor-pointer hover:bg-[#F8F6FF]' : ''} ${dragOverKey === key ? 'bg-[#EFEAFE]' : ''}`}
                                data-testid={`day-cell-${key}`}
                            >
                                <div className="flex items-center gap-1.5 mb-[5px]">
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); onDayClick?.(key); }}
                                        aria-label={`Open ${WEEKDAY_NAMES[p.weekday]} ${p.day} ${MONTH_NAMES[p.month]}`}
                                        className={`flex items-center justify-center w-[23px] h-[23px] rounded-full ${today ? 'bg-[#4D11F6] text-white text-xs font-semibold' : `text-[12.5px] font-medium hover:bg-[#F4F2EE] ${dim ? 'text-[#C0BAB4]' : 'text-[#3A3532]'}`}`}
                                        data-testid={`day-number-${key}`}
                                    >
                                        {p.day}
                                    </button>
                                    {p.day === 1 && <span className="text-[11px] text-[#A29B95]">{MONTH_NAMES[p.month].slice(0, 3)}</span>}
                                </div>
                                <div className="flex flex-col gap-[3px]">
                                    {visible.map(ev => (
                                        <EventTagChip key={ev.event_id} event={ev} onClick={onEventClick} draggable displayZone={displayZone} dimmed={dim} />
                                    ))}
                                    {overflow > 0 && (
                                        <DayOverflowPopover
                                            dayKey={key}
                                            todayKey={todayKey}
                                            events={dayEvents}
                                            onEventClick={onEventClick}
                                            onJumpToDay={onDayClick}
                                            displayZone={displayZone}
                                        >
                                            <button
                                                type="button"
                                                onClick={(e) => e.stopPropagation()}
                                                className="self-start mt-0.5 text-[10.5px] font-medium text-[#8A837E] hover:text-[#3A3532]"
                                                data-testid={`day-overflow-${key}`}
                                            >
                                                +{overflow} more
                                            </button>
                                        </DayOverflowPopover>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
